/**
 * What the user has already typed, and where they filed it.
 *
 * Suggesting a wording without its category only saves half the typing: the
 * category is the field that actually costs a thought. History answers both —
 * "Courses Carrefour" has been an expense in Courses eleven times.
 */

export interface DescriptionHistoryEntry {
  description: string | null;
  date: string;
  category_id: string | null;
  type: string;
}

export interface DescriptionSuggestion {
  label: string;
  /** How many past transactions carry this wording. */
  count: number;
  latestDate: string;
  /** The category it was filed under most often, or null when there is no usable answer. */
  categoryId: string | null;
}

/** Ranked by how often it was used, then by how recently. */
function mostUsed(tally: Map<string, { count: number; latestDate: string }>): string | null {
  let best: string | null = null;
  let bestCount = 0;
  let bestDate = '';

  for (const [id, stats] of tally) {
    if (stats.count > bestCount || (stats.count === bestCount && stats.latestDate > bestDate)) {
      best = id;
      bestCount = stats.count;
      bestDate = stats.latestDate;
    }
  }

  return best;
}

/**
 * Wordings from the user's own history matching what they are typing.
 *
 * Income and expense are kept apart: a category learnt on a refund has no
 * business landing on a purchase. Transfers get no suggestion at all — they
 * carry no category in clevia.
 *
 * `isUsableCategory` is what keeps a deleted or hidden category from coming
 * back through the back door; a wording whose category is gone is still
 * suggested, just without one.
 */
export function suggestDescriptions(
  query: string,
  type: 'income' | 'expense' | 'transfer',
  history: DescriptionHistoryEntry[],
  isUsableCategory: (categoryId: string) => boolean,
  limit = 5
): DescriptionSuggestion[] {
  const needle = query.trim().toLocaleLowerCase();
  if (!needle || type === 'transfer') return [];

  const grouped = new Map<
    string,
    {
      label: string;
      count: number;
      latestDate: string;
      categories: Map<string, { count: number; latestDate: string }>;
    }
  >();

  for (const entry of history) {
    if (entry.type !== type) continue;

    const label = entry.description?.trim();
    if (!label) continue;

    const key = label.toLocaleLowerCase();
    const existing = grouped.get(key);
    const group = existing ?? {
      label,
      count: 0,
      latestDate: entry.date,
      categories: new Map<string, { count: number; latestDate: string }>(),
    };

    group.count += 1;
    if (entry.date > group.latestDate) group.latestDate = entry.date;

    if (entry.category_id && isUsableCategory(entry.category_id)) {
      const seen = group.categories.get(entry.category_id);
      if (seen) {
        seen.count += 1;
        if (entry.date > seen.latestDate) seen.latestDate = entry.date;
      } else {
        group.categories.set(entry.category_id, { count: 1, latestDate: entry.date });
      }
    }

    if (!existing) grouped.set(key, group);
  }

  return [...grouped.values()]
    .filter((suggestion) => suggestion.label.toLocaleLowerCase().includes(needle))
    .sort(
      (a, b) =>
        b.count - a.count ||
        b.latestDate.localeCompare(a.latestDate) ||
        a.label.localeCompare(b.label)
    )
    .slice(0, limit)
    .map(({ label, count, latestDate, categories }) => ({
      label,
      count,
      latestDate,
      categoryId: mostUsed(categories),
    }));
}
